/**
 * The mutation inputs built from the Compare surfaces form.
 *
 * One result → that engine's own mutation, its payload equal to what the
 * legacy dialog sent (pinned by `__tests__/legacyParity.test.ts`). Both →
 * `startComparison`, which carries the report itself: the server refuses a
 * non-null `reportOptions` inside either half.
 *
 * Every feature gate is re-read here rather than trusted from the form: a
 * subscription can lapse while the dialog is open and these mutations cost
 * real compute, so the gate has the last word.
 */
import type {
  ReportOptionsInput,
  StartComparisonInput,
  StartDeviationAnalysisInput,
  StartSurfaceDifferenceInput,
} from '@/graphql/graphql';
import { isIfcModelLayer, isPointcloudLayer, type LayerLike } from '@/lib/analyses/candidates';
import { DEFAULT_MIN_COVERAGE_PCT, stationVolumesPayload, type StationVolumesDerived } from '@/lib/stationVolumes/dialogModel';
import { expectationPayload } from '@/lib/surfaceExpectation';

import { effectiveDeviationTolerance, type CompareFormValues } from './formSchema';

export interface CompareGates {
  /** `analysis.stationVolumes` */
  stationVolumes: boolean;
  /** `analysis.surfaceExpectation` */
  surfaceExpectation: boolean;
  /** `report.pdf` */
  reportPdf: boolean;
}

export interface PayloadContext {
  gates: CompareGates;
  /** The project's layers — the design's type decides the IFC / DSM params. */
  layers: readonly (LayerLike & { id: string })[];
  /** The dialog's own `deriveStationVolumes` result, reused rather than
   *  recomputed so the section's messages and the payload cannot disagree
   *  (D17). Absent = no station block. */
  stationDerived?: StationVolumesDerived;
  /** Carried through from a re-run (no control for it yet). */
  minCoveragePct?: number;
}

/** The report request, or `undefined` when none is wanted or allowed. The
 *  title falls back to the run name, so a blank title still prints one. */
function reportOptionsFor(v: CompareFormValues, ctx: PayloadContext): ReportOptionsInput | undefined {
  if (!v.addOns.pdf || !ctx.gates.reportPdf) return undefined;
  return {
    generate: true,
    title: v.addOns.reportTitle.trim() || v.output.name || undefined,
    notes: v.addOns.reportNotes.trim() || undefined,
    locale: v.addOns.reportLocale,
  };
}

function surfaceDifferenceInput(
  v: CompareFormValues,
  ctx: PayloadContext,
  report: ReportOptionsInput | undefined,
): StartSurfaceDifferenceInput {
  const { inputs, cutFill } = v;
  return {
    // Role A is always `top`: the design, or the LATER survey (§12.1), so
    // `dz = top − bottom > 0` reads as fill / ground added in both modes.
    top: {
      layerId: inputs.topLayerId,
      surfaceIds: inputs.topSurfaceIds.length ? inputs.topSurfaceIds : undefined,
      pcMethod: inputs.topPcMethod,
    },
    bottom: {
      layerId: inputs.bottomLayerId,
      surfaceIds: inputs.bottomSurfaceIds.length ? inputs.bottomSurfaceIds : undefined,
      pcMethod: inputs.bottomPcMethod,
    },
    name: v.output.name || undefined,
    boundaryLayerId: inputs.boundaryLayerId || undefined,
    params: {
      resolution: cutFill.resolution,
      colormap: cutFill.colormap,
      fillHoles: cutFill.fillHoles,
      exportDxf: v.addOns.dxf,
      exportTif: v.addOns.geotiff,
      compareKind: v.kind,
    },
    // The root-level station / expectation fields (R15) ride ONLY here: they
    // are cut & fill add-ons. `?? undefined`: NONE is an ABSENT key, not
    // `expectation: null`.
    expectation: ctx.gates.surfaceExpectation ? (expectationPayload(v) ?? undefined) : undefined,
    stationVolumes:
      ctx.gates.stationVolumes && ctx.stationDerived
        ? stationVolumesPayload(v, ctx.stationDerived, ctx.minCoveragePct ?? DEFAULT_MIN_COVERAGE_PCT)
        : undefined,
    ...(report ? { reportOptions: report } : {}),
  };
}

function deviationInput(
  v: CompareFormValues,
  ctx: PayloadContext,
  report: ReportOptionsInput | undefined,
): StartDeviationAnalysisInput {
  const d = v.deviation;
  const design = ctx.layers.find((l) => l.id === v.inputs.topLayerId);
  // Stale IFC filters / DSM settings from a previously picked design must not
  // ride along with a different one.
  const designIsIfc = isIfcModelLayer(design);
  const designIsCloud = !!design && isPointcloudLayer(design);
  return {
    sourceLayerId: v.inputs.bottomLayerId,
    referenceLayerId: v.inputs.topLayerId,
    boundaryLayerId: v.inputs.boundaryLayerId || undefined,
    name: v.output.name || undefined,
    // No `colormap`: the legacy dialog had no control for it and always sent
    // `Spectral_r`, which is `deviation.py --colormap`'s own default — so
    // leaving it out colours the LAZ / RGB GeoTIFF exactly as before.
    params: {
      mode: d.mode,
      min: d.min,
      max: d.max,
      decimate: d.decimate,
      verticalThreshold: d.verticalThreshold,
      kNearestTriangles: d.kNearestTriangles,
      signed: d.signed,
      ifcName: designIsIfc ? d.ifcName || undefined : undefined,
      ifcTypes: designIsIfc ? d.ifcTypes || undefined : undefined,
      outTif: v.addOns.geotiff,
      tifResolution: d.tifResolution,
      tifStat: d.tifStat,
      tifSrid: d.tifSrid ?? null,
      // The report's image section needs the per-surface renders.
      renderPerSurface: report ? true : d.renderPerSurface,
      renderImageSize: d.renderImageSize,
      renderColormap: d.renderColormap,
      volumeStats: d.volumeStats,
      volumeResolution: d.volumeResolution,
      // Horizontal mode: the default for a bad value, which nothing reads (R22).
      volumeTolerance: effectiveDeviationTolerance(d, designIsCloud),
      volumePlane: d.volumePlane,
      // Unedited → the server picks from the reference's feature count.
      referenceMode: d.referenceModeEdited ? d.referenceMode : undefined,
      referenceResolution: designIsCloud ? d.referenceResolution : undefined,
      referenceCellStat: designIsCloud ? d.referenceCellStat : undefined,
      referenceInterp: designIsCloud ? d.referenceInterp : undefined,
    },
    // Station volumes and the expectation are never sent here: they come
    // from cut & fill (spec goal 3).
    ...(report ? { reportOptions: report } : {}),
  };
}

/** `startSurfaceDifference` for a cut & fill-only run. */
export const buildSurfaceDifferenceInput = (v: CompareFormValues, ctx: PayloadContext): StartSurfaceDifferenceInput =>
  surfaceDifferenceInput(v, ctx, reportOptionsFor(v, ctx));

/** `startDeviationAnalysis` for a deviation-only run. */
export const buildDeviationInput = (v: CompareFormValues, ctx: PayloadContext): StartDeviationAnalysisInput =>
  deviationInput(v, ctx, reportOptionsFor(v, ctx));

/**
 * `startComparison` for a paired run: both halves, the report once on the
 * outside. The deviation half still forces its renders when that report is
 * on — the combined PDF's deviation section needs them.
 */
export function buildComparisonInput(v: CompareFormValues, ctx: PayloadContext): StartComparisonInput {
  const report = reportOptionsFor(v, ctx);
  const deviation = deviationInput(v, ctx, undefined);
  if (report) deviation.params = { ...deviation.params, renderPerSurface: true };
  return {
    surfaceDifference: surfaceDifferenceInput(v, ctx, undefined),
    deviation,
    ...(report ? { reportOptions: report } : {}),
  };
}

export type CompareSubmission =
  | { mutation: 'startSurfaceDifference'; input: StartSurfaceDifferenceInput }
  | { mutation: 'startDeviationAnalysis'; input: StartDeviationAnalysisInput }
  | { mutation: 'startComparison'; input: StartComparisonInput };

/**
 * Which mutation a submit fires: a single result keeps its own mutation
 * unchanged; only two results go through `startComparison` (spec §4.3).
 *
 * Never sends a deviation in Earlier-survey mode (§12.1) — `compareSchema`
 * refuses that form first (`deviationNeedsDesign`); this is the backstop.
 * Throws when nothing submittable is left (an empty selection, or a
 * deviation-only survey run), which the schema also refuses first.
 */
export function submissionFor(v: CompareFormValues, ctx: PayloadContext): CompareSubmission {
  const cutFill = v.results.includes('cutFill');
  const deviation = v.results.includes('deviation') && v.kind !== 'survey';
  if (!cutFill && !deviation) throw new Error('submissionFor: no result to submit');
  if (cutFill && deviation) return { mutation: 'startComparison', input: buildComparisonInput(v, ctx) };
  if (deviation) return { mutation: 'startDeviationAnalysis', input: buildDeviationInput(v, ctx) };
  return { mutation: 'startSurfaceDifference', input: buildSurfaceDifferenceInput(v, ctx) };
}
